const API_ROOT = '/api';

let csrfToken = '';

export class ApiError extends Error {
  constructor(message, status = 0, payload = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

async function readPayload(response) {
  const contentType = response.headers.get('content-type') || '';

  if (!contentType.includes('application/json')) {
    return null;
  }

  return response.json();
}

async function request(path, options = {}) {
  const method = String(options.method || 'GET').toUpperCase();
  const headers = new Headers(options.headers || {});

  headers.set('Accept', 'application/json');

  if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }

  if (!['GET', 'HEAD'].includes(method)) {
    if (!csrfToken) {
      await refreshCsrfToken();
    }

    headers.set('X-CSRF-Token', csrfToken);
  }

  const response = await fetch(`${API_ROOT}${path}`, {
    method,
    headers,
    credentials: 'same-origin',
    body:
      options.body === undefined
        ? undefined
        : JSON.stringify(options.body),
  });
  const payload = await readPayload(response);

  if (payload?.csrf_token) {
    csrfToken = payload.csrf_token;
  }

  if (!response.ok) {
    throw new ApiError(
      payload?.error ||
        `Le serveur a répondu avec le statut ${response.status}.`,
      response.status,
      payload
    );
  }

  return payload || {};
}

export async function refreshCsrfToken() {
  const response = await fetch(`${API_ROOT}/auth/csrf`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  const payload = await readPayload(response);

  if (!response.ok || !payload?.csrf_token) {
    throw new ApiError(
      payload?.error || 'Impossible d’initialiser la session sécurisée.',
      response.status,
      payload
    );
  }

  csrfToken = payload.csrf_token;
  return csrfToken;
}

export const ovhApi = {
  async session() {
    const payload = await request('/auth/me');
    csrfToken = payload.csrf_token || csrfToken;
    return payload.user;
  },

  async login(email, password) {
    const payload = await request('/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    return payload.user;
  },

  async logout() {
    await request('/auth/logout', { method: 'POST', body: {} });
    csrfToken = '';
  },

  async forgotPassword(email) {
    return request('/auth/forgot-password', {
      method: 'POST',
      body: { email },
    });
  },

  async tokenStatus(type, token) {
    return request('/auth/token-status', {
      method: 'POST',
      body: { type, token },
    });
  },

  async setPassword(type, token, password, confirmation) {
    return request('/auth/set-password', {
      method: 'POST',
      body: {
        type,
        token,
        password,
        password_confirmation: confirmation,
      },
    });
  },

  async changePassword(currentPassword, newPassword, confirmation) {
    return request('/auth/change-password', {
      method: 'POST',
      body: {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmation,
      },
    });
  },

  async shipments({ archived = false } = {}) {
    const query = new URLSearchParams({
      archived: archived ? '1' : '0',
    });
    const payload = await request(`/shipments?${query}`);
    return payload.shipments || [];
  },

  async createShipment(shipment) {
    const payload = await request('/shipments', {
      method: 'POST',
      body: shipment,
    });
    return payload.shipment;
  },

  async updateShipment(id, changes) {
    const payload = await request(`/shipments/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: changes,
    });
    return payload.shipment;
  },

  async deleteShipment(id) {
    return request(`/shipments/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: {},
    });
  },

  async bulkShipments(action, ids) {
    return request('/shipments/bulk', {
      method: 'POST',
      body: { action, ids },
    });
  },

  async registerPod(payload) {
    return request('/pods/register', {
      method: 'POST',
      body: payload,
    });
  },

  async applyPod(payload) {
    return request('/pods/apply', {
      method: 'POST',
      body: payload,
    });
  },

  async createPodReview(payload) {
    return request('/pods/review', {
      method: 'POST',
      body: payload,
    });
  },

  async decidePodReview(payload) {
    return request('/pods/review/decision', {
      method: 'POST',
      body: payload,
    });
  },

  async listUsers() {
    const payload = await request('/users');
    return payload.users || [];
  },

  async userAction(action, payload = {}) {
    return request('/users/action', {
      method: 'POST',
      body: { action, ...payload },
    });
  },

  async inviteUser(payload) {
    return request('/users/invite', {
      method: 'POST',
      body: payload,
    });
  },
};
